import React from 'react';
import ReactDOM from 'react-dom';
import firebase from 'firebase/app';
import prettyBytes from 'pretty-bytes';

/**
 * Task #8: spot duplicate uploads before they happen, and say where the existing copy is.
 *
 * Why it matters more than saving space: videos and assets are stored under their file name, so
 * uploading a second file with the same name REPLACES the first one, even in another project.
 * Same name -> skip, or upload as a renamed copy. Same size, different name -> probably the same
 * file renamed; skip or upload anyway.
 */

export type DuplicateKind = 'videos' | 'assets' | 'gallery';

export type ExistingCopy = {
  reason: 'same name' | 'same size';
  name: string;
  size?: number;
  date?: number;
  projectID?: string;
  projectTitle?: string;
  recordTitle?: string;
};

const db = () => firebase.firestore();

const toNumber = (v: unknown) => (v == null || v === '' ? undefined : Number(v));

async function describe(reason: ExistingCopy['reason'], doc: firebase.firestore.DocumentSnapshot): Promise<ExistingCopy> {
  const d = doc.data() || {};
  const copy: ExistingCopy = { reason, name: d.fileName || d.id || doc.id, size: toNumber(d.size), date: toNumber(d.date) };
  try {
    let projectID: string | undefined = d.projectID;
    if (d.recordID) {
      const rec = await db().collection('records').doc(d.recordID).get();
      copy.recordTitle = rec.get('title') || undefined;
      projectID = projectID || rec.get('projectID');
    }
    if (projectID) {
      copy.projectID = projectID;
      const proj = await db().collection('projects').doc(projectID).get();
      copy.projectTitle = proj.get('title') || undefined;
    }
  } catch (e) {
    // Location details are a nice-to-have; the warning still shows without them.
  }
  return copy;
}

/** Existing copies of `file` already in the database. */
export async function findDuplicates(kind: DuplicateKind, file: File, projectID?: string): Promise<ExistingCopy[]> {
  const found: ExistingCopy[] = [];
  const seen = new Set<string>();
  const col = db().collection(kind);

  if (kind === 'gallery') {
    // Gallery files no longer overwrite each other, so only flag the same image twice in one project.
    if (!projectID) return found;
    const same = await col.where('projectID', '==', projectID).where('id', '==', file.name).limit(3).get();
    for (const d of same.docs) found.push(await describe('same name', d));
    return found;
  }

  const byName = await col.doc(file.name).get();
  if (byName.exists) {
    seen.add(byName.id);
    found.push(await describe('same name', byName));
  }
  // Sizes are stored as numbers, and on some old records as strings: check both.
  const [asNumber, asText] = await Promise.all([
    col.where('size', '==', file.size).limit(5).get(),
    col.where('size', '==', String(file.size)).limit(5).get(),
  ]);
  for (const d of asNumber.docs.concat(asText.docs)) {
    if (seen.has(d.id)) continue;
    seen.add(d.id);
    found.push(await describe('same size', d));
  }
  return found;
}

/** "Clip.mp4" -> "Clip (copy 2).mp4", the first name not already taken. */
export async function renamedCopy(kind: DuplicateKind, file: File): Promise<File> {
  const dot = file.name.lastIndexOf('.');
  const stem = dot > 0 ? file.name.slice(0, dot) : file.name;
  const ext = dot > 0 ? file.name.slice(dot) : '';
  for (let n = 2; n < 100; n++) {
    const name = `${stem} (copy ${n})${ext}`;
    const taken = await db().collection(kind).doc(name).get();
    if (!taken.exists) return new File([file], name, { type: file.type, lastModified: file.lastModified });
  }
  throw new Error('Could not find a free name for the copy.');
}

// ---- the dialog --------------------------------------------------------------

export type DuplicateChoice = 'skip' | 'copy' | 'anyway';

let queue: Promise<unknown> = Promise.resolve();

/** Ask what to do. Dialogs for several dropped files are shown one after another. */
export function askAboutDuplicate(fileName: string, copies: ExistingCopy[]): Promise<DuplicateChoice> {
  const run = () =>
    new Promise<DuplicateChoice>((resolve) => {
      const host = document.createElement('div');
      document.body.appendChild(host);
      const close = (choice: DuplicateChoice) => {
        ReactDOM.unmountComponentAtNode(host);
        host.remove();
        resolve(choice);
      };
      ReactDOM.render(<DuplicateDialog fileName={fileName} copies={copies} onChoose={close} />, host);
    });
  const next = queue.then(run, run);
  queue = next.catch(() => undefined);
  return next;
}

const button = (primary: boolean): React.CSSProperties => ({
  height: 44,
  padding: '0 16px',
  borderRadius: 10,
  border: primary ? 'none' : '1px solid #DADDE2',
  background: primary ? '#16181D' : '#FFFFFF',
  color: primary ? '#FFFFFF' : '#16181D',
  font: 'inherit',
  fontSize: 14,
  fontWeight: primary ? 600 : 400,
  cursor: 'pointer',
});

const DuplicateDialog: React.FC<{ fileName: string; copies: ExistingCopy[]; onChoose(c: DuplicateChoice): void }> = ({
  fileName,
  copies,
  onChoose,
}) => {
  const sameName = copies.some((c) => c.reason === 'same name');
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        background: 'rgba(22,24,29,0.55)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        fontFamily: 'inherit',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="dup-title"
        style={{ background: '#FFFFFF', borderRadius: 16, maxWidth: 560, width: '100%', boxShadow: '0 24px 60px rgba(0,0,0,0.25)' }}
      >
        <div style={{ padding: '20px 24px 8px' }}>
          <h2 id="dup-title" style={{ margin: 0, fontSize: 19, color: '#16181D' }}>
            {sameName ? 'This file is already in the database' : 'This might be a file you already have'}
          </h2>
          <p style={{ margin: '6px 0 0', fontSize: 14, color: '#5B616E', wordBreak: 'break-word' }}>
            <strong style={{ color: '#16181D' }}>{fileName}</strong>
            {sameName
              ? ' has the same name as the file below. Uploading it as it is would replace that file, so it can only be added as a renamed copy.'
              : ' is exactly the same size as the file below, which usually means it is the same file with a different name.'}
          </p>
        </div>
        <ul style={{ listStyle: 'none', margin: '12px 24px', padding: 0, border: '1px solid #E4E6EA', borderRadius: 12 }}>
          {copies.map((c, i) => (
            <li key={i} style={{ padding: '10px 14px', borderTop: i ? '1px solid #EEF0F2' : 'none', fontSize: 14 }}>
              <div style={{ fontWeight: 600, color: '#16181D', wordBreak: 'break-word' }}>{c.name}</div>
              <div style={{ color: '#5B616E', fontSize: 13, marginTop: 2 }}>
                {c.reason === 'same name' ? 'Same name' : 'Same size'}
                {c.size ? ` · ${prettyBytes(c.size)}` : ''}
                {c.date
                  ? ` · added ${new Date(c.date < 1e11 ? c.date * 1000 : c.date).toLocaleDateString('en-AU', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}`
                  : ''}
              </div>
              {c.projectID && (
                <a
                  href={`/project/${c.projectID}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'inline-block', marginTop: 4, fontSize: 13, fontWeight: 600, color: '#B8045A' }}
                >
                  Open {c.projectTitle || 'the project'}
                  {c.recordTitle ? ` › ${c.recordTitle}` : ''} in a new tab →
                </a>
              )}
            </li>
          ))}
        </ul>
        <div style={{ padding: '8px 24px 20px', display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button type="button" style={button(false)} onClick={() => onChoose(sameName ? 'copy' : 'anyway')}>
            {sameName ? 'Upload as a renamed copy' : 'Upload anyway'}
          </button>
          <button type="button" style={button(true)} onClick={() => onChoose('skip')} autoFocus>
            Skip this file
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * One call for the upload screens: returns the file to upload (possibly renamed), or null to skip.
 * If the check itself fails, it asks rather than risking an overwrite.
 */
export async function checkBeforeUpload(kind: DuplicateKind, file: File, projectID?: string): Promise<File | null> {
  let copies: ExistingCopy[];
  try {
    copies = await findDuplicates(kind, file, projectID);
  } catch (e) {
    const ok = window.confirm(
      `Couldn't check whether "${file.name}" is already in the database. Upload it anyway? A file with the same name would be replaced.`,
    );
    return ok ? file : null;
  }
  if (copies.length === 0) return file;
  const choice = await askAboutDuplicate(file.name, copies);
  if (choice === 'skip') return null;
  if (choice === 'copy') return kind === 'gallery' ? file : renamedCopy(kind, file);
  return file;
}
